import { AlignJustify, Rows3, Columns3, Search, X } from "lucide-react";

export type FeedbackViewMode = "timeline" | "swimlane" | "compact";

const FEEDBACK_TYPES = [
  { key: "progress", label: "Progress", color: "text-brand-cyan bg-brand-cyan/10" },
  { key: "completion", label: "Done", color: "text-brand-green bg-brand-green/10" },
  { key: "error", label: "Error", color: "text-brand-pink bg-brand-pink/10" },
  { key: "decision", label: "Decision", color: "text-brand-purple bg-brand-purple/10" },
  { key: "observation", label: "Observe", color: "text-brand-cyan bg-brand-cyan/10" },
  { key: "artifact", label: "Artifact", color: "text-yellow-400 bg-yellow-400/10" },
  { key: "request-for-input", label: "Input", color: "text-amber-400 bg-amber-400/10" },
  { key: "confidence", label: "Conf", color: "text-text-secondary bg-surface-lighter/30" },
] as const;

interface FeedbackToolbarProps {
  viewMode: FeedbackViewMode;
  onViewModeChange: (mode: FeedbackViewMode) => void;
  activeFilters: Set<string>;
  onToggleFilter: (type: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
  messageCount: number;
  onClear: () => void;
}

export function FeedbackToolbar({
  viewMode,
  onViewModeChange,
  activeFilters,
  onToggleFilter,
  search,
  onSearchChange,
  messageCount,
  onClear,
}: FeedbackToolbarProps) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-border-subtle/15 shrink-0">
      {/* View mode toggle */}
      <div className="flex items-center bg-surface/40 rounded-lg p-0.5 gap-0.5">
        <ViewModeButton
          icon={AlignJustify}
          active={viewMode === "timeline"}
          onClick={() => onViewModeChange("timeline")}
          tooltip="Timeline"
        />
        <ViewModeButton
          icon={Columns3}
          active={viewMode === "swimlane"}
          onClick={() => onViewModeChange("swimlane")}
          tooltip="Swimlane"
        />
        <ViewModeButton
          icon={Rows3}
          active={viewMode === "compact"}
          onClick={() => onViewModeChange("compact")}
          tooltip="Compact"
        />
      </div>

      {/* Separator */}
      <div className="w-px h-5 bg-border-subtle/20" />

      {/* Type filters */}
      <div className="flex items-center gap-1 flex-1 overflow-x-auto">
        {FEEDBACK_TYPES.map((t) => {
          const isActive = activeFilters.size === 0 || activeFilters.has(t.key);
          return (
            <button
              key={t.key}
              onClick={() => onToggleFilter(t.key)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap transition-[color,background-color,transform] duration-200 active:scale-[0.96] ${
                isActive
                  ? t.color
                  : "text-text-muted/40 bg-transparent"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-text-muted" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter..."
          className="w-28 bg-surface/40 border border-border-subtle/15 rounded-lg pl-7 pr-2 py-1 text-[11px] text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-purple/30 focus:w-40 transition-[border-color,width] duration-200"
        />
      </div>

      {/* Count + clear */}
      <span className="text-[10px] text-text-muted font-mono shrink-0 tabular-nums">
        {messageCount}
      </span>
      {messageCount > 0 && (
        <button
          onClick={onClear}
          className="text-text-muted hover:text-text-primary transition-[color,transform] duration-200 active:scale-[0.96]"
          title="Clear all"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function ViewModeButton({
  icon: Icon,
  active,
  onClick,
  tooltip,
}: {
  icon: any;
  active: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <button
      onClick={onClick}
      title={tooltip}
      className={`p-1.5 rounded-md transition-[color,background-color,transform] duration-200 active:scale-[0.96] ${
        active
          ? "bg-brand-purple/15 text-brand-purple"
          : "text-text-muted hover:text-text-primary hover:bg-surface-lighter/20"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
